/**
 * A Vorgang that answers 404 — handed over, or never mine — named the same
 * way wherever it is met: the record, the board, the calculation (round-5
 * screen check, R5-6: the board said only „Vorgang nicht gefunden" where the
 * record said whom it went to). In `lib/` because three features read it and
 * features do not import each other (R-W1).
 */
import { useQueryClient } from "@tanstack/react-query";

import { RECORD_KEY, useHandoverNotices, type CaseRecordOut, type HandoverNoticeOut } from "@/api";

export const GONE_TITLE = "Vorgang nicht gefunden";
export const HANDED_OVER_TITLE = "Vorgang übergeben";
export const GONE_TEXT =
  "Diesen Vorgang gibt es nicht — oder er liegt inzwischen bei jemand anderem. Deine Vorgänge stehen im Register.";

export interface HandedTo {
  name: string;
  title: string;
}

/**
 * Whom a Vorgang that answers 404 went to, when the last record seen of it
 * says so (UX gate round 1, #14): it carried an open request to that person,
 * and a request of mine is the one way a Vorgang of mine becomes somebody
 * else's. A record never seen knows nothing.
 */
export function newOwnerOf(lastSeen: CaseRecordOut | undefined): HandedTo | null {
  const open = lastSeen?.handover ?? null;
  if (lastSeen === undefined || open === null || open.state !== "pending") return null;
  return { name: open.toDisplayName, title: lastSeen.case.title };
}

/**
 * The same, from the sender's handover notices (round-2 screen check, R2-3):
 * a request of mine that was accepted leaves an unread notice with the
 * Vorgang's id, title and the new owner — so a page opened fresh, by direct
 * URL or after a reload, can name them too, as long as the notice has not
 * been put away on the start page.
 */
export function acceptedFor(
  caseId: string | undefined,
  notices: readonly HandoverNoticeOut[] | undefined,
): HandedTo | null {
  const found = notices?.find(
    (notice) => notice.handover.caseId === caseId && notice.handover.state === "accepted",
  );
  return found === undefined ? null : { name: found.handover.toDisplayName, title: found.caseTitle };
}

/** Title and sentence of the gone state — named where the new owner is known. */
export function goneWords(handedTo: HandedTo | null): { title: string; text: string } {
  if (handedTo === null) return { title: GONE_TITLE, text: GONE_TEXT };
  return {
    title: HANDED_OVER_TITLE,
    text: `„${handedTo.title}“ liegt jetzt bei ${handedTo.name} — die Übergabe ist angenommen. Deine Vorgänge stehen im Register.`,
  };
}

/**
 * Whom a gone Vorgang went to, for a page that holds no record of its own
 * (the board, the calculation): the record's last answer where the cache
 * still has it, else the sender's notices — asked only once the Vorgang is
 * gone and the cache could not say, and fresh then. `asking` while that
 * answer is on its way, so the general sentence does not stand for a moment
 * before the named one replaces it.
 */
export function useHandedTo(caseId: string | undefined, gone: boolean): { handedTo: HandedTo | null; asking: boolean } {
  const client = useQueryClient();
  const lastSeen = gone && caseId !== undefined ? client.getQueryData<CaseRecordOut>(RECORD_KEY(caseId)) : undefined;
  const cached = gone ? newOwnerOf(lastSeen) : null;
  const notices = useHandoverNotices({ forGone: gone && cached === null });
  const handedTo = cached ?? (gone ? acceptedFor(caseId, notices.data) : null);
  return { handedTo, asking: gone && handedTo === null && notices.isFetching };
}
